import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { toolsAdapter } from '../agent/tools.adapter';
import { useDashboardContext } from '../hooks/useDashboardContext';
import AgentMemoryService from '../services/agentMemoryService';
import { logger } from '../lib/logger';

export default function ProactiveAgent() {
  const location = useLocation();
  
  // Only run on dashboard routes
  const isDashboardRoute = (path) => {
    const dashboardPaths = ['/command-center'];
    return dashboardPaths.some((p) => path.startsWith(p)) || path.includes('/dashboard');
  };
  
  const ctx = useDashboardContext();
  // Memoize on primitive values, NOT the ctx object reference.
  // ctx is a new object every render (useNavigate returns unstable ref),
  // so [ctx] would cause identity to change every render → infinite useEffect loop.
  const contractorId = ctx?.companyId || ctx?.tenantId || 'default';
  const jobsiteId = ctx?.currentJobsite?.id || 'default';
  const userId = ctx?.user?.uid || 'default';

  const identity = useMemo(
    () => ({ contractorId, jobsiteId, userId }),
    [contractorId, jobsiteId, userId]
  );

  const [autonomy, setAutonomy] = useState({
    userAutopilot: 'off',
    riskTolerance: 'medium',
    requireConfirmHighRisk: true,
  });
  const executedRef = useRef(new Set());
  const timerRef = useRef(null);

  // Initialize dashboard context for tools adapter
  useEffect(() => {
    try {
      toolsAdapter.setDashboardContext(ctx);
    } catch (_e) {
      // Log quietly in dev; do not crash UI
      logger.debug('ProactiveAgent: setDashboardContext failed', {
        context: { error: _e.message },
      });
    }
  }, [ctx]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const a = await AgentMemoryService.getAutonomy({
          contractorId: identity.contractorId,
          userId: identity.userId,
        });
        if (mounted) setAutonomy(a);
      } catch (_e) {
        logger.debug('ProactiveAgent: getAutonomy failed', { context: { error: _e.message } });
      }
    })();
    return () => {
      mounted = false;
    };
  }, [identity]);

  // Use a ref for ctx so the interval doesn't reset on every ctx change.
  // ctx is a new object every render; depending on it would clear/recreate the interval each render.
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;

  useEffect(() => {
    // Clear previous timer
    if (timerRef.current) clearInterval(timerRef.current);
    if (autonomy.userAutopilot === 'off') return;

    const intervalMs = 60000; // 60s polling
    timerRef.current = setInterval(async () => {
      try {
        const currentCtx = ctxRef.current;
        const { suggestions } = await AgentMemoryService.getSuggestions(identity, {
          metrics: currentCtx?.metrics,
          workers: currentCtx?.workers?.length || 0,
        });
        if (!Array.isArray(suggestions) || suggestions.length === 0) return;

        for (const s of suggestions) {
          const key = `${s.id}`;
          if (executedRef.current.has(key)) continue;

          // Gating by risk level and autopilot mode
          const risk = s.risk || 'low';
          const allow =
            autonomy.userAutopilot === 'full'
              ? risk !== 'high' // full: low+medium
              : autonomy.userAutopilot === 'low'
                ? risk === 'low'
                : false; // low: low only
          if (!allow) continue;

          try {
            await toolsAdapter.exec(s.command, s.payload, currentCtx);
            executedRef.current.add(key);
          } catch (_e) {
            logger.debug('ProactiveAgent: exec suggestion failed', {
              context: { error: _e.message },
            });
          }
        }
      } catch (_e) {
        logger.debug('ProactiveAgent: getSuggestions failed', { context: { error: _e.message } });
      }
    }, intervalMs);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [autonomy, identity]);

  // Don't run on non-dashboard pages
  if (!isDashboardRoute(location.pathname)) {
    return null;
  }

  return null;
}
